import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Lock,
  Bell,
  Database,
  Save,
  Trash2,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  platformSettingsService,
  PlatformSettings,
} from '@/features/branding/services/platform-settings.service';
import {
  subscriptionsAdminService,
  Plan,
} from '@/features/branding/services/subscriptions.service';

export const Route = createFileRoute('/system/settings')({
  component: SettingsPage,
});

// ─── Seção de configuração ────────────────────────────────────────────────────

function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-indigo-400" />
          <CardTitle className="text-base text-white">{title}</CardTitle>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

// ─── Linha de configuração ────────────────────────────────────────────────────

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-200">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

function SettingsPage() {
  const queryClient = useQueryClient();
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<PlatformSettings>>({});
  const [isDirty, setIsDirty] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => platformSettingsService.getAll(),
  });

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['plans-list'],
    queryFn: () => subscriptionsAdminService.listPlans(),
  });

  // Sincronizar draft com os dados carregados
  useEffect(() => {
    if (settings) {
      setDraft(settings);
      setIsDirty(false);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => platformSettingsService.update(draft),
    onSuccess: (updated) => {
      queryClient.setQueryData(['platform-settings'], updated);
      // Invalidar o cache público para que o banner atualize
      queryClient.invalidateQueries({ queryKey: ['public-platform-settings'] });
      setIsDirty(false);
      toast.success('Configurações salvas com sucesso');
    },
    onError: () => toast.error('Erro ao salvar configurações'),
  });

  const purgeMutation = useMutation({
    mutationFn: () => platformSettingsService.purgeLogs(),
    onSuccess: (res) => {
      setPurgeConfirmOpen(false);
      toast.success(`${res.deleted} log${res.deleted !== 1 ? 's' : ''} removido${res.deleted !== 1 ? 's' : ''}`);
    },
    onError: () => toast.error('Erro ao executar purge'),
  });

  const set = <K extends keyof PlatformSettings>(key: K, value: PlatformSettings[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const val = <K extends keyof PlatformSettings>(key: K): PlatformSettings[K] =>
    (draft[key] ?? settings?.[key]) as PlatformSettings[K];

  if (isLoading || !settings) {
    return (
      <div className="p-6 flex items-center justify-center h-48 text-gray-500 text-sm">
        Carregando configurações...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Configurações da Plataforma</h1>
          <p className="text-sm text-gray-400 mt-1">
            Controle global do comportamento da plataforma
          </p>
        </div>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending}
          className="bg-indigo-600 hover:bg-indigo-500 gap-2 disabled:opacity-40"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? 'Salvando...' : 'Salvar alterações'}
        </Button>
      </div>

      {isDirty && (
        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-900/20 border border-yellow-800 rounded-lg text-xs text-yellow-300">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          Há alterações não salvas
        </div>
      )}

      {/* ── Acesso & Segurança ──────────────────────────────────────────────── */}
      <SettingsSection
        icon={Lock}
        title="Acesso & Segurança"
        description="Controle quem pode criar novas contas e qual plano é atribuído"
      >
        <SettingRow
          label="Registro público habilitado"
          description="Permite que qualquer pessoa crie uma nova empresa em /register"
        >
          <Switch
            checked={val('public_signup_enabled')}
            onCheckedChange={(v) => set('public_signup_enabled', v)}
            className="data-[state=checked]:bg-indigo-600"
          />
        </SettingRow>

        <Separator className="bg-gray-800" />

        <SettingRow
          label="Plano padrão no signup"
          description="Plano atribuído automaticamente quando um tenant se cadastra via registro público"
        >
          <Select
            value={val('default_plan_id') ?? 'none'}
            onValueChange={(v) => set('default_plan_id', v === 'none' ? null : v)}
          >
            <SelectTrigger className="w-44 bg-gray-800 border-gray-700 text-gray-100 text-sm">
              <SelectValue placeholder="Nenhum" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-gray-100">
              <SelectItem value="none" className="focus:bg-gray-700">
                Nenhum
              </SelectItem>
              {plans.filter((p) => p.isActive).map((plan) => (
                <SelectItem
                  key={plan.id}
                  value={plan.id}
                  className="focus:bg-gray-700"
                >
                  {plan.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>
      </SettingsSection>

      {/* ── Comunicação & Manutenção ────────────────────────────────────────── */}
      <SettingsSection
        icon={Bell}
        title="Comunicação & Manutenção"
        description="Mensagens e avisos exibidos para todos os usuários da plataforma"
      >
        <SettingRow
          label="Email de suporte"
          description="Exibido nas páginas de erro e emails automáticos"
        >
          <Input
            type="email"
            placeholder="suporte@plataforma.com"
            value={val('support_email')}
            onChange={(e) => set('support_email', e.target.value)}
            className="w-56 bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-600 text-sm"
          />
        </SettingRow>

        <Separator className="bg-gray-800" />

        <SettingRow
          label="Modo manutenção"
          description="Exibe um banner de aviso no topo de todas as páginas da plataforma"
        >
          <Switch
            checked={val('maintenance_mode')}
            onCheckedChange={(v) => set('maintenance_mode', v)}
            className="data-[state=checked]:bg-yellow-600"
          />
        </SettingRow>

        <div className="space-y-2">
          <Label className="text-sm text-gray-300">Mensagem do banner</Label>
          <Input
            placeholder="Ex: Sistema em manutenção programada das 22h às 23h."
            value={val('maintenance_message')}
            onChange={(e) => set('maintenance_message', e.target.value)}
            className="bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-600 text-sm"
          />
          {val('maintenance_message') && (
            <div className="flex items-center gap-2 mt-2">
              <Eye className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
              <div className="flex-1 bg-yellow-500 text-yellow-950 text-xs text-center py-1.5 px-3 rounded font-medium">
                {val('maintenance_message')}
              </div>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* ── Dados & Retenção ────────────────────────────────────────────────── */}
      <SettingsSection
        icon={Database}
        title="Dados & Retenção"
        description="Política de armazenamento dos logs de auditoria da plataforma"
      >
        <SettingRow
          label="Retenção de audit logs"
          description="Logs mais antigos que este período serão removidos no próximo purge"
        >
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={7}
              max={3650}
              value={val('audit_log_retention_days')}
              onChange={(e) => set('audit_log_retention_days', Number(e.target.value))}
              className="w-20 bg-gray-800 border-gray-700 text-gray-100 text-sm text-center"
            />
            <span className="text-sm text-gray-400">dias</span>
          </div>
        </SettingRow>

        <Separator className="bg-gray-800" />

        <SettingRow
          label="Purge manual"
          description={`Remove agora todos os logs com mais de ${val('audit_log_retention_days')} dias`}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPurgeConfirmOpen(true)}
            className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-950 border border-red-900"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Executar purge
          </Button>
        </SettingRow>
      </SettingsSection>

      {/* Dialog de confirmação do purge */}
      <Dialog open={purgeConfirmOpen} onOpenChange={setPurgeConfirmOpen}>
        <DialogContent size="sm" busy={purgeMutation.isPending} className="bg-gray-900 border-gray-700 text-gray-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="h-5 w-5" />
              Confirmar purge de logs
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400 leading-relaxed">
            Todos os audit logs com mais de{' '}
            <span className="text-gray-200 font-medium">
              {val('audit_log_retention_days')} dias
            </span>{' '}
            serão permanentemente removidos. Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPurgeConfirmOpen(false)}
              className="border-gray-600 bg-transparent text-gray-300 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => purgeMutation.mutate()}
              loading={purgeMutation.isPending}
              className="bg-red-600 hover:bg-red-500 text-white gap-2"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Executar purge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
